/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState, useCallback, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
import {Box, Button, Flex, useMultiStyleConfig, Text} from '@chakra-ui/react'
import {ListMenuPopover} from './list-menu-popover'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * Submenus open when users click a trigger or focus it with Tab and press Enter or Space.
 */

const ListMenu = ({setIsListMenuOpen}) => {
    const [ariaBusy, setAriaBusy] = useState(true)
    const [selectedMenuItem, setSelectedMenuItem] = useState(null)
    const [visibleMenuItems, setVisibleMenuItems] = useState(() => new Set())
    const [bandElement, setBandElement] = useState(null)
    const [bandHeight, setBandHeight] = useState(0)
    const selectedMenuItemRef = useRef(selectedMenuItem)
    const intl = useIntl()
    const {data} = useGetMainMenu()
    const {locale} = useMultiSite()
    const categoriesData = useGetCategoriesData()
    const styles = useMultiStyleConfig('ListMenu')
    const isMenuVisible = selectedMenuItem !== null || visibleMenuItems.size > 0
    selectedMenuItemRef.current = selectedMenuItem

    useEffect(() => {
        setIsListMenuOpen?.(isMenuVisible)
    }, [isMenuVisible, setIsListMenuOpen])

    const handleMenuVisibilityChange = useCallback((menuItemId, isVisible) => {
        setVisibleMenuItems((current) => {
            if (current.has(menuItemId) === isVisible) {
                return current
            }

            const next = new Set(current)
            if (isVisible) {
                next.add(menuItemId)
            } else {
                next.delete(menuItemId)
            }
            return next
        })
    }, [])

    const handleMenuSelectionChange = useCallback((menuItemId) => {
        selectedMenuItemRef.current = menuItemId
        setSelectedMenuItem(menuItemId)
    }, [])

    const handleMenuHeightChange = useCallback((menuItemId, height) => {
        if (selectedMenuItemRef.current === menuItemId) {
            setBandHeight(height)
        }
    }, [])

    const handleCardClick = useCallback(() => {
        handleMenuSelectionChange(null)
    }, [handleMenuSelectionChange])

    const handleNavMouseLeave = useCallback(() => {
        handleMenuSelectionChange(null)
    }, [handleMenuSelectionChange])

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    /*  useEffect(() => {
        if (!data?.primarycategories?.length) {
            return
        }

        const navigableCategories = data.primarycategories.filter(
            (primarycategory) => primarycategory?._content_type_uid !== 'firstlevelsimplelink',
        )
        const templateFourCategory = navigableCategories.find(
            (primarycategory) => primarycategory?._content_type_uid === 'menu_redesign_template_4',
        )
        const defaultCategory =
            templateFourCategory ?? navigableCategories[1] ?? navigableCategories[0]

        //setSelectedMenuItem(defaultCategory?.uid ?? null)
    }, [data]) */

    return (
        <nav
            id="list-menu"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation',
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            {data && categoriesData && (
                <Flex {...styles.container} onMouseLeave={handleNavMouseLeave} position="relative">
                    <Box
                        ref={setBandElement}
                        data-testid="list-menu-band"
                        position="absolute"
                        top="100%"
                        left="calc((100% - 100vw) / 2)"
                        width="100vw"
                        height={`${selectedMenuItem !== null ? bandHeight : 0}px`}
                        overflow="hidden"
                        background="background.base.default"
                        visibility={isMenuVisible ? 'visible' : 'hidden'}
                        pointerEvents={selectedMenuItem !== null ? 'auto' : 'none'}
                        transition={
                            selectedMenuItem !== null
                                ? 'height 320ms cubic-bezier(0.05, 0.7, 0.1, 1)'
                                : 'height 260ms cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                        zIndex={-1}
                    />
                    {data.primarycategories.map((primarycategory) => {
                        if (primarycategory._content_type_uid === 'firstlevelsimplelink') {
                            const {url, label, isAbsolute} = getGlobalLink({
                                link: primarycategory.redirects,
                                categoriesData,
                                locale: locale?.alias,
                            })
                            return (
                                <Box
                                    key={primarycategory.uid}
                                    onMouseEnter={() => handleMenuSelectionChange(null)}
                                    {...styles.listMenuTriggerContainer}
                                >
                                    {isAbsolute ? (
                                        <Button
                                            as="a"
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={handleCardClick}
                                            variant="ghost"
                                            size="base"
                                        >
                                            <Text
                                                color={
                                                    primarycategory?.label_hex_color ??
                                                    'foreground.accent.default'
                                                }
                                            >
                                                {label}
                                            </Text>
                                        </Button>
                                    ) : (
                                        <Button
                                            as={RouteLink}
                                            to={url}
                                            onClick={handleCardClick}
                                            variant="ghost"
                                            size="base"
                                        >
                                            <Text
                                                color={
                                                    primarycategory?.label_hex_color ??
                                                    'foreground.accent.default'
                                                }
                                            >
                                                {label}
                                            </Text>
                                        </Button>
                                    )}
                                </Box>
                            )
                        }
                        return (
                            <ListMenuPopover
                                selectedMenuItem={selectedMenuItem}
                                setSelectedMenuItem={handleMenuSelectionChange}
                                bandElement={bandElement}
                                onHeightChange={handleMenuHeightChange}
                                onVisibilityChange={handleMenuVisibilityChange}
                                key={primarycategory.uid}
                                primarycategory={primarycategory}
                                categoriesData={categoriesData}
                                onItemClick={handleCardClick}
                            />
                        )
                    })}
                    <Box
                        {...styles.popoverOverlay}
                        visibility={isMenuVisible ? 'visible' : 'hidden'}
                        opacity={selectedMenuItem !== null ? 1 : 0}
                        transition="opacity 200ms cubic-bezier(0, 0, 0.2, 1)"
                        pointerEvents={selectedMenuItem !== null ? 'auto' : 'none'}
                        aria-hidden="true"
                        onMouseEnter={handleNavMouseLeave}
                    />
                </Flex>
            )}
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {setIsListMenuOpen: PropTypes.func}

export {ListMenu}
